import { Button as AntdButton } from 'antd'
import Flex from 'components/Flex'
import React from 'react'
import styled, { css } from 'styled-components'
import { COLORS } from 'theme'

const BorderStyle = css`
  &:hover {
    border-color: ${COLORS.GREY_T_85};
  }
  &:focus {
    border-color: ${COLORS.GREY_T_85};
  }
`
const TextContainer = styled.div`
  margin-left: ${props => (props.icon ? '7px' : '')};
  font-weight: 600;
  font-size: ${props => (props.fontsize ? props.fontsize : 'initial')};
`
const dangerButtonStyles = css`
  background-color: ${COLORS.RED_T_90};
  border-color: ${COLORS.RED_T_20};
  color: ${COLORS.RED_S_100};
  &:hover {
    background-color: ${COLORS.RED_T_90};
    border-color: ${COLORS.RED_T_20};
    color: ${COLORS.RED_S_100};
  }
  &:focus {
    background-color: ${COLORS.RED_T_90};
    border-color: ${COLORS.RED_T_20};
    color: ${COLORS.RED_S_100};
  }
  ${props =>
    props.disable &&
    css`
      background-color: #f1f1f1;
      border-color: #d1d5db;
      color: #b8b8b8;
      cursor: not-allowed;
      pointer-events: none;
    `}
`
const DisconnectButtonStyles = css`
  background-color: #fcefed;
  border-color: #fcefed;
  color: #4d566e;
  &:hover {
    background-color: #fcefed;
    border-color: #fcefed;
    color: #4d566e;
  }
  &:focus {
    background-color: #fcefed;
    border-color: #fcefed;
    color: #4d566e;
  }
`
const OutLineButtonStyles = css`
  background-color: #fffffd;
  border-color: #fcefed;
  color: ${COLORS.PRIMARY};
  &:hover {
    border-color: #fcefed;
    color: #4d566e;
  }
  &:focus {
    border-color: #fcefed;
    color: #4d566e;
  }
`
const DashedButtonStyles = css`
  background-color: #F9FAFE
  border: 2px dashed #d6dbf7;
  color: ${COLORS.PRIMARY};
  &:hover {
    background-color: #F9FAFE;
    border: 2px dashed #d6dbf7;
    color: #3249d7;
  }
  &:focus {
    background-color:#F9FAFE;
    border: 2px dashed #d6dbf7;
    color: #3249d7;
  }
`

const secondaryButtonStyles = css`
  background-color: ${props =>
    props.backgroundColor ? props.backgroundColor : COLORS.PRIMARY_PURPLE};
  border-color: ${COLORS.PURPLE_T_80};
  color: '#FFFFFF';
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
  &:hover {
    background-color: ${COLORS.PRIMARY_PURPLE};
    border-color: ${COLORS.PURPLE_T_80};
  }
  &:focus {
    background-color: ${COLORS.PRIMARY_PURPLE};
    border-color: ${COLORS.PURPLE_T_80};
  }
  ${props =>
    props.disable &&
    css`
      background-color: #f1f1f1;
      border-color: #d1d5db;
      color: #b8b8b8;
      cursor: not-allowed;
      pointer-events: none;
    `}
`

const ExportButtonStyles = css`
  background-color: #fff;
  border: 1px solid #5db594;
  color: #5db594;
  &:hover {
    background-color: #fff;
    border: 1px solid #5db594;
  }
  &:focus {
    background-color: #fff;
    border: 1px solid #5db594;
  }
`
const defaultButtonStyles = css`
  color: #30475e;
`

const PlainButton = styled(AntdButton)`
  width: ${props => (props.width ? props.width : 'auto')};
  height: ${props => (props.height ? props.height : '40px')};
  margin-right: ${props => (props.marginRight ? props.marginRight : '')};
  color: ${props =>
    props.type === 'secondary' && props.color
      ? '#4D566E'
      : props.type === 'secondary'
      ? COLORS.PRIMARY
      : '#FFFFFF'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  justify-content: center;
  cursor: ${props => props?.disableCursor && 'not-allowed'};
  pointer-events: ${props => (props?.disable ? 'none' : 'auto')};
  ${BorderStyle}
  ${props => props.type === 'primary' && PrimaryButtonStyles};
  ${props => props.type === 'secondary' && secondaryButtonStyles}
  ${props => props.type === 'danger' && dangerButtonStyles};
  ${props => props.type === 'default' && defaultButtonStyles};
  ${props => props.type === 'disconnect' && DisconnectButtonStyles};
  ${props => props.type === 'outline' && OutLineButtonStyles};
  ${props => props.type === 'dashed' && DashedButtonStyles};
  ${props => props.type === 'export' && ExportButtonStyles};
  ${BorderStyle}
  white-space: ${props => (props.wordBreak ? 'break-spaces' : '')};
  ${props =>
    props.disable &&
    css`
      background-color: #f1f1f1;
      border-color: #d1d5db;
      color: #b8b8b8;
      cursor: not-allowed;
      pointer-events: none;
    `}
`
const CircleButton = styled(AntdButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${BorderStyle}
`

const Button = props => <AntdButton {...props} />

const PrimaryButtonStyles = css`
  background-color: #3249d7;
  color: #f9fafe;
  &:hover {
    background-color: #3249d7;
    color: #f9fafe;
  }
  &:focus {
    background-color: #3249d7;
    color: #f9fafe;
  }
`
const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 16px;
    width: 16px;
    vertical-align: middle; // ✅ keeps SVG in line
  }
`;

const Primary = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="primary">
    {icon}
    <TextContainer icon={!!icon} fontsize={props.fontsize}>
      {text}
    </TextContainer>
  </PlainButton>
)

const Secondary = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="secondary">
    <Flex centerVertically className="secondary_icon_container">
      {icon}
      <TextContainer
        fontsize={props?.fontsize}
        icon={!!icon}
        className="secondary_text_container"
      >
        {text}
      </TextContainer>
    </Flex>
  </PlainButton>
)

const Export = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="export">
    <Flex>
      {icon}
      <TextContainer icon={!!icon}>{text}</TextContainer>
    </Flex>
  </PlainButton>
)

const Danger = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="danger">
    <Flex>
    <IconWrapper>{icon}</IconWrapper>
    <TextContainer icon={icon}>{text}</TextContainer>
    </Flex>
  </PlainButton>
)

const Disconnect = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="disconnect">
    <Flex>
      {icon}
      <TextContainer icon>{text}</TextContainer>
    </Flex>
  </PlainButton>
)
const Outline = ({ text, icon, ...props }) => (
  <PlainButton {...props} type="outline">
    <Flex>
      <TextContainer>{text}</TextContainer>
    </Flex>
  </PlainButton>
)

const Default = ({ icon, text, onClick, ...rest }) => {
  return (
    <PlainButton onClick={onClick} {...rest} type="default">
      {icon}
      <TextContainer icon={icon}>{text}</TextContainer>
    </PlainButton>
  )
}

const Round = ({ icon, text, onClick, ...rest }) => {
  return (
    <PlainButton type="round" onClick={onClick} {...rest}>
      {icon}
      <TextContainer>{text}</TextContainer>
    </PlainButton>
  )
}

const Circle = ({ icon, onClick, ...rest }) => {
  return (
    <CircleButton type="circle" onClick={onClick} {...rest}>
      {icon}
    </CircleButton>
  )
}

const Dashed = ({ icon, text, onClick, ...rest }) => {
  return (
    <PlainButton onClick={onClick} {...rest} type="dashed">
      {icon}
      <TextContainer icon={icon}>{text}</TextContainer>
    </PlainButton>
  )
}

Button.Primary = Primary
Button.Secondary = Secondary
Button.Danger = Danger
Button.Default = Default
Button.Round = Round
Button.Circle = Circle
Button.Disconnect = Disconnect
Button.Outline = Outline
Button.Dashed = Dashed
Button.Export = Export

export default Button
